import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import salesInvoiceService, {
  chartOfAccountsService,
  SalesInvoice,
  SalesInvoiceItem,
  SalesInvoiceGlAccounts,
  OpenAdvanceRow,
  OpenCreditNoteRow,
  AdvanceAllocationRow,
  CreditNoteAllocationRow,
  ApplyAdvanceAllocationInput,
} from '../services/salesInvoice.service';
import salesOrderService, { CreateAdvanceInvoicePayload } from '../services/salesOrder.service';
import { getFinanceApiErrorMessage } from '@/utils/financeApiError';
import { useAuthStore } from '@/store/authStore';

export const useSalesInvoices = (filters?: any): UseQueryResult<SalesInvoice[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', filters],
    queryFn: () => salesInvoiceService.getAll(filters),
  });
};

export const useSalesInvoice = (id: string): UseQueryResult<SalesInvoice, Error> => {
  return useQuery({
    queryKey: ['sales-invoices', id],
    queryFn: () => salesInvoiceService.getById(id),
    enabled: !!id,
  });
};

export const useSalesInvoiceItems = (id: string): UseQueryResult<SalesInvoiceItem[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', id, 'items'],
    queryFn: () => salesInvoiceService.getItems(id),
    enabled: !!id,
  });
};

export const useCreateSalesInvoice = (): UseMutationResult<
  SalesInvoice,
  Error,
  { invoice: Partial<SalesInvoice>; items: Partial<SalesInvoiceItem>[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ invoice, items }) => salesInvoiceService.create(invoice, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      message.success('Tax Invoice created successfully');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to create Tax Invoice');
    },
  });
};

export const useCreateAdvanceInvoice = (): UseMutationResult<
  SalesInvoice,
  Error,
  { salesOrderId: string; payload: CreateAdvanceInvoicePayload }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ salesOrderId, payload }) => salesOrderService.createAdvanceInvoice(salesOrderId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      message.success('Advance Invoice created');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to create Advance Invoice');
    },
  });
};

export const useDeliveriesEligibleForInvoice = (
  customerId: string | null,
  salesOrderId?: string | null
) => {
  return useQuery({
    queryKey: ['deliveries-eligible-invoice', customerId, salesOrderId ?? ''],
    queryFn: () =>
      salesInvoiceService.getDeliveriesEligibleForInvoice(customerId!, salesOrderId || undefined),
    enabled: !!customerId,
  });
};

export const useDeleteDraftSalesInvoice = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesInvoiceService.deleteDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries-eligible-invoice'] });
      message.success('Draft invoice deleted');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to delete draft invoice');
    },
  });
};

export const useCreateSalesInvoiceFromDeliveries = (): UseMutationResult<
  SalesInvoice,
  Error,
  { customerId: string; deliveryNoteIds: string[]; departmentId?: string }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ customerId, deliveryNoteIds, departmentId }) =>
      salesInvoiceService.createInvoiceFromDeliveries(customerId, deliveryNoteIds, departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries-eligible-invoice'] });
      message.success('Tax Invoice created from deliveries');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to create invoice from deliveries');
    },
  });
};

/** POST /sales/invoices/from-sales-order — draft invoice from SO (backend rules apply). */
export const useCreateSalesInvoiceFromSO = (): UseMutationResult<SalesInvoice, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (salesOrderId: string) => salesInvoiceService.createFromSalesOrder(salesOrderId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      const soId = data?.sales_order_id;
      if (soId) {
        queryClient.invalidateQueries({ queryKey: ['sales-orders', soId] });
        queryClient.invalidateQueries({ queryKey: ['sales-orders', soId, 'status-check'] });
      }
      message.success('Tax invoice created');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to create invoice from customer order');
    },
  });
};

export const useUpdateSalesInvoice = (): UseMutationResult<
  SalesInvoice,
  Error,
  { id: string; invoice: Partial<SalesInvoice>; items: Partial<SalesInvoiceItem>[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, invoice, items }) => salesInvoiceService.update(id, invoice, items),
    onSuccess: (data, variables) => {
      if (data) queryClient.setQueryData(['sales-invoices', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id, 'items'] });
      const soId = (data as { sales_order_id?: string })?.sales_order_id;
      if (soId) {
        queryClient.invalidateQueries({ queryKey: ['sales-orders', soId, 'status-check'] });
      }
      message.success('Tax Invoice updated');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to update Tax Invoice');
    },
  });
};

export const useUpdateSalesInvoiceAddresses = (): UseMutationResult<
  SalesInvoice,
  Error,
  { id: string; customer_address?: string | null; shipping_address?: string | null }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, customer_address, shipping_address }) =>
      salesInvoiceService.updateAddresses(id, { customer_address, shipping_address }),
    onSuccess: (data, variables) => {
      if (data) queryClient.setQueryData(['sales-invoices', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sales-customers'] });
      if (data?.customer_id) {
        queryClient.invalidateQueries({ queryKey: ['sales-customer', data.customer_id] });
      }
      message.success('Addresses saved (Customer Master updated)');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to update addresses');
    },
  });
};

export const useUpdateSalesInvoiceGlAccounts = (): UseMutationResult<
  SalesInvoice,
  Error,
  { id: string } & SalesInvoiceGlAccounts
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, ...accounts }) => salesInvoiceService.updateGlAccounts(id, accounts),
    onSuccess: (data, variables) => {
      if (data) queryClient.setQueryData(['sales-invoices', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id] });
      message.success('GL accounts saved');
    },
    onError: (error: Error) => {
      message.error(getFinanceApiErrorMessage(error, 'Failed to save GL accounts'));
    },
  });
};

export const useApproveSalesInvoice = (): UseMutationResult<SalesInvoice, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesInvoiceService.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
      message.success('Invoice approved');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to approve');
    },
  });
};

export const usePostSalesInvoice = (): UseMutationResult<SalesInvoice, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesInvoiceService.post(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
      message.success('Invoice posted');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to post invoice');
    },
  });
};

export const usePostSalesInvoiceToGL = (): UseMutationResult<
  void,
  Error,
  { id: string; accounts?: SalesInvoiceGlAccounts }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, accounts }) => salesInvoiceService.postToGL(id, accounts),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id] });
      message.success('Invoice posted to GL');
    },
    onError: (error: Error) => {
      message.error(getFinanceApiErrorMessage(error, 'Failed to post to GL'));
    },
  });
};

export const useUnpostSalesInvoiceFromGL = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesInvoiceService.unpostFromGL(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<SalesInvoice>(['sales-invoices', id], (old) =>
        old ? { ...old, gl_posted: false } : old
      );
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
      message.success('Invoice unposted from GL');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to unpost from GL');
    },
  });
};

export const useRecordInvoicePayment = (): UseMutationResult<
  SalesInvoice,
  Error,
  { id: string; payment_amount: number }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payment_amount }) => salesInvoiceService.recordPayment(id, payment_amount),
    onSuccess: (data, variables) => {
      if (data) queryClient.setQueryData(['sales-invoices', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.id] });
      message.success('Payment recorded');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to record payment');
    },
  });
};

export const useCancelSalesInvoice = (): UseMutationResult<SalesInvoice, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesInvoiceService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
      message.success('Invoice cancelled');
    },
    onError: (error: Error) => {
      message.error(error?.message ?? 'Failed to cancel invoice');
    },
  });
};

export const useIncomeAccounts = (): UseQueryResult<{ id: string; account_code: string; account_name: string }[], Error> => {
  return useQuery({
    queryKey: ['chart-of-accounts', 'Income'],
    queryFn: () => chartOfAccountsService.getIncomeAccounts(false),
  });
};

export const useChartAccountsByType = (
  accountType: string,
  isGroup: boolean = false
): UseQueryResult<{ id: string; account_code: string; account_name: string }[], Error> => {
  // Company-scope the cache — switching company must not reuse another COA list.
  const companyId = useAuthStore((s) => s.user?.company_id);
  return useQuery({
    queryKey: ['chart-of-accounts', companyId || '', accountType, isGroup],
    queryFn: () => chartOfAccountsService.getAccountsByType(accountType, isGroup),
    enabled: !!accountType && !!companyId,
  });
};

export const useOpenAdvances = (customerId: string | null): UseQueryResult<OpenAdvanceRow[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', 'open-advances', customerId],
    queryFn: () => salesInvoiceService.getOpenAdvances(customerId!),
    enabled: !!customerId,
  });
};

export const useOpenCreditNotes = (customerId: string | null): UseQueryResult<OpenCreditNoteRow[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', 'open-credit-notes', customerId],
    queryFn: () => salesInvoiceService.getOpenCreditNotes(customerId!),
    enabled: !!customerId,
  });
};

export const useAdvanceAllocations = (
  invoiceId: string | null,
  enabled?: boolean
): UseQueryResult<AdvanceAllocationRow[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', invoiceId, 'advance-allocations'],
    queryFn: () => salesInvoiceService.getAdvanceAllocations(invoiceId!),
    enabled: !!invoiceId && (enabled !== false),
  });
};

export const useCreditNoteAllocations = (
  invoiceId: string | null,
  enabled?: boolean
): UseQueryResult<CreditNoteAllocationRow[], Error> => {
  return useQuery({
    queryKey: ['sales-invoices', invoiceId, 'credit-note-allocations'],
    queryFn: () => salesInvoiceService.getCreditNoteAllocations(invoiceId!),
    enabled: !!invoiceId && (enabled !== false),
  });
};

export const useApplyAdvances = (): UseMutationResult<
  { invoice: SalesInvoice; allocations: AdvanceAllocationRow[] },
  Error,
  { invoiceId: string; allocations: ApplyAdvanceAllocationInput[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ invoiceId, allocations }) => salesInvoiceService.applyAdvances(invoiceId, allocations),
    onSuccess: (data, variables) => {
      if (data?.invoice) queryClient.setQueryData(['sales-invoices', variables.invoiceId], data.invoice);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.invoiceId, 'advance-allocations'] });
      const soId = data?.invoice?.sales_order_id;
      if (soId) queryClient.invalidateQueries({ queryKey: ['sales-orders', soId, 'status-check'] });
      message.success('Customer advances applied');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to apply advances');
    },
  });
};

export const useApplyCreditNotes = (): UseMutationResult<
  { invoice: SalesInvoice; allocations: CreditNoteAllocationRow[] },
  Error,
  { invoiceId: string; allocations: { customer_credit_note_id: string; amount: number }[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ invoiceId, allocations }) => salesInvoiceService.applyCreditNotes(invoiceId, allocations),
    onSuccess: (data, variables) => {
      if (data?.invoice) queryClient.setQueryData(['sales-invoices', variables.invoiceId], data.invoice);
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices', variables.invoiceId, 'credit-note-allocations'] });
      const soId = data?.invoice?.sales_order_id;
      if (soId) queryClient.invalidateQueries({ queryKey: ['sales-orders', soId, 'status-check'] });
      message.success('Credit notes applied');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to apply credit notes');
    },
  });
};
